import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCustomers, fetchPoolByCustomer, updateCustomer, deleteCustomer, fetchAllWorkOrders, fetchEmployees, postBulkUpload, fetchTaskRecords, fetchGateCodes } from '../../Utils/api';
import { Group as GroupIcon } from "@mui/icons-material";
import DataTable from '../../Components/DataTable/data-table'; // Reusable DataTable component
import './viewcustomers.css';
import PopupMenu from '../../Components/Popup/pop-up';
import CustomerModal from '../../Components/CustomerForm/customer-form';
import PopupGeneric from '../../Components/PopupGeneric/pop-up-generic';
import WorkOrdersPopup from '../../Components/WorkOrderPopup/WorkOrderPopup';
import CustomerMap from '../../Components/CustomerMap/CustomerMap';
import AdvancedFilters from '../../Components/AdvancedFilters/AdvancedFilters';
import CustomerAnalytics from '../../Components/CustomerAnalytics/CustomerAnalytics';
import BulkActionsToolbar from '../../Components/BulkActionsToolbar/BulkActionsToolbar';
import { Button, TextField, InputAdornment, ToggleButtonGroup, ToggleButton, Box, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Typography, Paper, Grid, Chip, Fade, Grow } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MapIcon from '@mui/icons-material/Map';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useAuth } from '../../Auth/AuthContext';
import * as XLSX from 'xlsx';
import { validateEmail, validateZip, formatPhone, sanitizeCity, decodeHtmlEntities } from '../../Utils/validations';

const normalizePhone = (phone) => {
  if (phone === null || phone === undefined || phone === '') return '';
  let digits = String(phone).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.substring(1);
  return digits;
};


const ViewCustomers = () => {
  const navigate = useNavigate();
  const dataTableRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'all', 'active', 'inactive' - default to active only
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Advanced filters state
  const [healthStatusFilter, setHealthStatusFilter] = useState([]);
  const [serviceDaysFilter, setServiceDaysFilter] = useState([]);
  const [cityFilter, setCityFilter] = useState([]);

  // Bulk selection state
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [isPopupGenericVisible, setPopupGenericVisible] = useState(false);
  const [popupGenericTitle, setPopupGenericTitle] = useState('');
  const [popupGenericMessage, setPopupGenericMessage] = useState('');
  const [showWorkOrders, setShowWorkOrders] = useState(false);

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customerEmployeeInfo, setCustomerEmployeeInfo] = useState(null);
  // Active gate codes for the customer currently shown in the View popup.
  const [customerGateCodes, setCustomerGateCodes] = useState([]);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const {companyId} = useAuth();

  // Phase 1 enhancements - Customer metrics and health status
  const [customerMetrics, setCustomerMetrics] = useState({
    total: 0,
    newThisMonth: 0,
    active: 0,
    healthyPercentage: 0
  });
  const [customerHealthStatus, setCustomerHealthStatus] = useState({});

  const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Handle metric card click - scroll to data table and apply filters
  const handleMetricClick = (filterType) => {
    // Switch to list view if currently in map view
    if (viewMode !== 'list') {
      setViewMode('list');
    }

    // Apply appropriate filters based on which metric was clicked
    switch (filterType) {
      case 'total':
        setStatusFilter('all');
        setHealthStatusFilter([]);
        break;
      case 'active':
        setStatusFilter('active');
        setHealthStatusFilter([]);
        break;
      case 'healthy':
        setStatusFilter('all');
        setHealthStatusFilter(['healthy']);
        break;
      case 'newThisMonth':
        // For new this month, show all customers sorted by ID (newest first)
        setStatusFilter('all');
        setHealthStatusFilter([]);
        break;
      default:
        break;
    }

    // Scroll to the data table
    setTimeout(() => {
      if (dataTableRef.current) {
        dataTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Calculate customer health status based on last service date
  const getHealthStatus = (lastServiceDate) => {
    if (!lastServiceDate) return 'none';

    const today = new Date();
    const serviceDate = new Date(lastServiceDate);
    const daysSinceService = Math.floor((today - serviceDate) / (1000 * 60 * 60 * 24));

    if (daysSinceService < 7) return 'healthy'; // Green
    if (daysSinceService < 14) return 'warning'; // Yellow
    return 'overdue'; // Red
  };

  const getHealthColor = (status) => {
    switch(status) {
      case 'healthy': return '#10B981';
      case 'warning': return '#F59E0B';
      case 'overdue': return '#EF4444';
      default: return '#9CA3AF';
    }
  };

  const getHealthLabel = (status) => {
    switch(status) {
      case 'healthy': return 'Recent';
      case 'warning': return 'Due Soon';
      case 'overdue': return 'Overdue';
      default: return 'No History';
    }
  };

  // Derive service day and assigned technician from active weekly work orders
  const customerScheduleMap = useMemo(() => {
    const map = {};
    if (!workOrders.length || !employees.length) return map;
    workOrders.forEach(wo => {
      if (!wo.workOrder) return;
      if (wo.workOrder.statusCode?.toLowerCase() === 'cancelled') return;
      if ((wo.workOrder.workOrderOccurance ?? 0) !== 7) return;
      const custId = wo.workOrder.customerId;
      const dateStr = (wo.workOrder.workOrderDueDate?.trim() || wo.workOrder.initialServiceDate?.trim() || '');
      if (!dateStr) return;
      const woDate = new Date(dateStr.split('T')[0] + 'T12:00:00');
      if (map[custId] && map[custId].woDate > woDate) return;
      const dayName = DAYS_OF_WEEK[woDate.getDay()];
      const emp = employees.find(e => e.employeeId === wo.workOrder.employeeId);
      const techName = emp ? `${emp.firstName} ${emp.lastName}`.trim() : null;
      map[custId] = { serviceDay: dayName, techName, woDate };
    });
    return map;
  }, [workOrders, employees]);

  const columns = [
    {
      field: 'name',
      headerName: 'Customer Name',
      headerAlign: 'center',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'contact',
      headerName: 'Phone',
      headerAlign: 'center',
      width: 145,
      minWidth: 145,
    },
    {
      field: 'email',
      headerName: 'Email',
      headerAlign: 'center',
      flex: 1.2,
      minWidth: 190,
    },
    {
      field: 'serviceDay',
      headerName: 'Service Day',
      headerAlign: 'center',
      width: 115,
      minWidth: 100,
      renderCell: (params) => {
        const info = customerScheduleMap[params.row.id];
        return info?.serviceDay || params.row.serviceDay || '—';
      },
    },
    {
      field: 'assignedTechnician',
      headerName: 'Assigned Technician',
      headerAlign: 'center',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        const info = customerScheduleMap[params.row.id];
        return info?.techName || '—';
      },
    },
    {
      field: 'healthStatus',
      headerName: 'Service Status',
      headerAlign: 'center',
      width: 130,
      minWidth: 120,
      renderCell: (params) => {
        const healthStatus = customerHealthStatus[params.row.id] || 'none';
        const tooltipText = {
          'healthy': 'Serviced 0-6 days ago',
          'warning': 'Serviced 7-13 days ago (due soon)',
          'overdue': 'Serviced 14+ days ago (overdue)',
          'none': 'No service history recorded'
        };
        return (
          <Tooltip title={tooltipText[healthStatus]} arrow placement="top">
            <Chip
              label={getHealthLabel(healthStatus)}
              size="small"
              sx={{
                backgroundColor: getHealthColor(healthStatus),
                color: 'white',
                fontWeight: 600,
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                animation: 'fadeIn 0.3s ease-in',
                cursor: 'help'
              }}
            />
          </Tooltip>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      minWidth: 330,
      width: 330,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            gap: '6px',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEditClick(params.row.custObject)}
            sx={{ fontSize: '0.7rem', padding: '4px 10px', whiteSpace: 'nowrap' }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleViewMoreClick(params.row.custObject)}
            sx={{ fontSize: '0.7rem', padding: '4px 10px', whiteSpace: 'nowrap' }}
          >
            View
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleLogsClick(params.row.custObject)}
            sx={{ fontSize: '0.7rem', padding: '4px 10px', whiteSpace: 'nowrap' }}
          >
            Logs
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => handleDeleteClick(params.row.custObject)}
            sx={{ fontSize: '0.7rem', padding: '4px 10px', whiteSpace: 'nowrap' }}
          >
            {params.row.status === 'Active' ? 'Deactivate' : 'Delete'}
          </Button>
        </Box>
      ),
    },
  ];


  const loadCustomers = useCallback(async () => {
    if (!companyId) {
      return;
    }

    try {
      // Always fetch ALL customers for metrics calculation
      // The statusFilter will be applied only for display purposes in the table
      const data = await fetchCustomers(companyId, null, null);

      // Fetch task records to calculate health status
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      let allTaskRecords = [];
      try {
        allTaskRecords = await fetchTaskRecords({
          companyId: companyId,
          startDate: startDate,
          endDate: endDate
        });
      } catch (error) {
        console.error('Error fetching task records for health status:', error);
      }

      // Calculate health status for each customer
      const healthStatusMap = {};
      const lastServiceDates = {};

      data.forEach(customer => {
        // Find tasks for this customer
        const customerTasks = allTaskRecords.filter(task =>
          task.customer && task.customer.customerId === customer.customerId
        );

        if (customerTasks.length > 0) {
          // First, check for overdue incomplete tasks (highest priority for health status)
          const incompleteTasks = customerTasks.filter(task => {
            const status = task.taskDetails?.taskStatus;
            return status !== 'Completed' && status !== 'Cancelled';
          });

          const today = new Date();
          const overdueTasks = incompleteTasks.filter(task => {
            const dueDate = task.taskDetails?.workOrderTaskDueDate || task.taskDate;
            return dueDate && new Date(dueDate) < today;
          });

          if (overdueTasks.length > 0) {
            // Find the most overdue task (oldest due date)
            const sortedOverdue = overdueTasks.sort((a, b) => {
              const dateA = a.taskDetails?.workOrderTaskDueDate || a.taskDate;
              const dateB = b.taskDetails?.workOrderTaskDueDate || b.taskDate;
              return new Date(dateA) - new Date(dateB); // Oldest first
            });

            const mostOverdueTask = sortedOverdue[0];
            const overdueDate = mostOverdueTask.taskDetails?.workOrderTaskDueDate || mostOverdueTask.taskDate;
            lastServiceDates[customer.customerId] = overdueDate;
            healthStatusMap[customer.customerId] = getHealthStatus(overdueDate);
          } else {
            // No overdue tasks - check if there are any completed tasks
            const completedTasks = customerTasks.filter(task =>
              task.taskDetails?.taskStatus === 'Completed'
            );

            if (completedTasks.length > 0) {
              // Find the most recently completed task
              const sortedCompleted = completedTasks.sort((a, b) => {
                const dateA = a.taskDetails?.workOrderTaskDueDate || a.taskDate;
                const dateB = b.taskDetails?.workOrderTaskDueDate || b.taskDate;
                return new Date(dateB) - new Date(dateA); // Most recent first
              });

              const lastCompletedTask = sortedCompleted[0];
              const completedDate = lastCompletedTask.taskDetails?.workOrderTaskDueDate || lastCompletedTask.taskDate;
              lastServiceDates[customer.customerId] = completedDate;
              healthStatusMap[customer.customerId] = getHealthStatus(completedDate);
            } else {
              // Has tasks but none completed or overdue (all future tasks)
              healthStatusMap[customer.customerId] = 'healthy';
            }
          }
        } else {
          healthStatusMap[customer.customerId] = 'none';
        }
      });

      setCustomerHealthStatus(healthStatusMap);

      const formattedData = data.map((customer) => ({
        id: customer.customerId,
        name: `${customer.custFirstName} ${customer.custLastName}`,
        contact: formatPhone(customer.custContactNo),
        email: customer.custEmail || '',
        address: `${customer.custAddress}, ${customer.custCity}, ${customer.custZip}`,
        serviceDay: customer.serviceDay,
        status: customer.currRecInd === "Y" ? "Active" : "Inactive",
        custObject: customer,
      }));

      setCustomers(formattedData);

      // Calculate metrics
      const activeCustomers = data.filter(c => c.currRecInd === 'Y');

      // Count customers with recent IDs as "new this month" (approximate)
      const customerIds = data.map(c => c.customerId).sort((a, b) => b - a);
      const newThisMonth = customerIds.length > 0 ? Math.min(customerIds.length, Math.ceil(customerIds.length * 0.1)) : 0;

      // Calculate healthy percentage
      const healthyCounts = Object.values(healthStatusMap).reduce((acc, status) => {
        if (status === 'healthy') acc.healthy++;
        if (status !== 'none') acc.total++;
        return acc;
      }, { healthy: 0, total: 0 });

      const healthyPercentage = healthyCounts.total > 0
        ? Math.round((healthyCounts.healthy / healthyCounts.total) * 100)
        : 0;

      setCustomerMetrics({
        total: data.length,
        newThisMonth: newThisMonth,
        active: activeCustomers.length,
        healthyPercentage: healthyPercentage
      });

    } catch (error) {
      console.error('loadCustomers ERROR:', error);
      console.error('loadCustomers ERROR stack:', error.stack);
    }
  }, [companyId]);

  const fetchPoolDetails = async (id) => {
    try {
      const response = await fetchPoolByCustomer(companyId, id);
      if (response) {
        return response;
      }
    }
    catch (error) {
      console.error("Error fetching pool details: ", error);
      return null;
    }
  };

  const loadWorkOrdersAndEmployees = useCallback(async () => {
    if (!companyId) {
      return;
    }

    try {
      const [ordersData, employeesData] = await Promise.all([
        fetchAllWorkOrders(companyId),
        fetchEmployees(companyId, null, null)
      ]);
      setWorkOrders(ordersData);
      setEmployees(employeesData);
    } catch (error) {
      console.error('Error loading routes and employees:', error);
    }
  }, [companyId]);

  const handleCancelEdit = () => {
  setEditModalOpen(false);
  setCustomerToEdit(null);
};

  const handleAddCustomer = () => {
    navigate('/add-customer');
  };


  useEffect(() => {
    loadCustomers();
    loadWorkOrdersAndEmployees();
  }, [loadCustomers, loadWorkOrdersAndEmployees]);


  // Filter customers based on search query, status, and advanced filters
  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch = customer.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && customer.status === 'Active') ||
      (statusFilter === 'inactive' && customer.status === 'Inactive');

    // Advanced filters
    const customerHealthStatus2 = customerHealthStatus[customer.id] || 'none';
    const matchesHealthStatus = healthStatusFilter.length === 0 || healthStatusFilter.includes(customerHealthStatus2);

    const effectiveServiceDay = customerScheduleMap[customer.id]?.serviceDay;
    const matchesServiceDay = serviceDaysFilter.length === 0 || serviceDaysFilter.includes(effectiveServiceDay);

    const customerCity = customer.custObject?.custCity;
    const matchesCity = cityFilter.length === 0 || cityFilter.includes(customerCity);

    return matchesSearch && matchesStatus && matchesHealthStatus && matchesServiceDay && matchesCity;
  });
  // Handle row double-click
  const handleViewMoreClick = async (customer) => {
    setSelectedCustomer(customer);

    // Load this customer's gate codes for the detail popup. Gate codes now live in their
    // own table (multiple per customer), so they are fetched separately from the customer
    // record. Reset first so a previously viewed customer's codes never flash here.
    setCustomerGateCodes([]);
    try {
      const codes = await fetchGateCodes(customer.customerId);
      setCustomerGateCodes(Array.isArray(codes) ? codes : []);
    } catch (error) {
      console.error('Error fetching gate codes for customer details:', error);
      setCustomerGateCodes([]);
    }

    // Find active routes for this customer
    const customerWorkOrders = workOrders.filter(wo =>
      wo.workOrder &&
      wo.workOrder.customerId === customer.customerId &&
      wo.workOrder.statusCode !== 'Cancelled'
    );

    if (customerWorkOrders.length > 0) {
      // Get the first active route
      const workOrder = customerWorkOrders[0];

      // Get day of week from initialServiceDate
      let dayOfWeek = null;
      if (workOrder.workOrder.initialServiceDate) {
        try {
          const serviceDate = new Date(workOrder.workOrder.initialServiceDate);
          const dayIndex = serviceDate.getDay();
          dayOfWeek = DAYS_OF_WEEK[dayIndex];
        } catch (error) {
          console.error('Error parsing service date:', error);
        }
      }

      // Fetch task records to get the currently assigned technician and last service date.
      // Include 90 days into the future so upcoming scheduled tasks (created by Employee Scheduling)
      // are captured — a reassigned employee's task is always in the future until they service the pool.
      // The descending date sort puts the furthest-future task first, which reflects the current assignment.
      let formattedLastServiceDate = null;
      let assignedEmployeeName = null;
      try {
        const today = new Date();
        const startDate = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const endDate = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        const taskRecords = await fetchTaskRecords({
          companyId: companyId,
          startDate: startDate,
          endDate: endDate,
          customerId: customer.customerId
        });

        if (taskRecords && taskRecords.length > 0) {
          const sortedTasks = taskRecords.sort((a, b) => {
            const dateA = a.taskDetails?.workOrderTaskDueDate || a.taskDate;
            const dateB = b.taskDetails?.workOrderTaskDueDate || b.taskDate;
            return new Date(dateB) - new Date(dateA);
          });

          // The task with the latest date (top of descending sort) reflects the current assignment
          const latestTask = sortedTasks[0];
          if (latestTask.employee) {
            const emp = latestTask.employee;
            assignedEmployeeName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || null;
          }

          // Last service date comes from the most recent PAST completed task, keyed off the
          // actual completion timestamp (checkOut/checkIn) rather than the scheduled due date —
          // these diverge whenever a visit is completed on a different day than scheduled, which
          // showed the wrong (earlier) date here. Matches the same precedence used in Reports.
          const actualServiceDateStr = (t) =>
            t.taskDetails?.checkOut || t.taskDetails?.checkIn || t.taskDetails?.updtTs ||
            t.taskDetails?.creaTs || t.taskDetails?.workOrderTaskDueDate || t.taskDate;

          const pastCompletedTasks = sortedTasks
            .filter((t) => {
              const taskDate = new Date(actualServiceDateStr(t));
              return taskDate <= today && t.taskDetails?.taskStatus === 'Completed';
            })
            .sort((a, b) => new Date(actualServiceDateStr(b)) - new Date(actualServiceDateStr(a)));

          const lastCompletedTask = pastCompletedTasks[0];
          if (lastCompletedTask) {
            const lastServiceDateStr = actualServiceDateStr(lastCompletedTask);
            const lastServiceDate = new Date(lastServiceDateStr);
            const dayIndex = lastServiceDate.getDay();
            const dayName = DAYS_OF_WEEK[dayIndex];
            formattedLastServiceDate = `${dayName}, ${lastServiceDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}`;
          }
        }
      } catch (error) {
        console.error('Error fetching task records for customer details:', error);
      }

      // Fall back to the work order's employee only if no task-based assignment was found
      if (!assignedEmployeeName) {
        const employee = employees.find(emp => emp.employeeId === workOrder.workOrder.employeeId);
        assignedEmployeeName = employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown';
      }

      setCustomerEmployeeInfo({
        employeeName: assignedEmployeeName,
        dayOfWeek: dayOfWeek,
        lastServiceDate: formattedLastServiceDate
      });
    } else {
      setCustomerEmployeeInfo(null);
    }

    setPopupVisible(true);
  };

  const handleLogsClick = (customer) => {
    // Navigate to Reports page with customer data
    navigate('/reports', {
      state: {
        customer: {
          id: customer.customerId,
          name: `${customer.custFirstName} ${customer.custLastName}`,
          address: `${customer.custAddress}, ${customer.custCity}, ${customer.custZip}`,
        }
      }
    });
  };

  const handleEditClick = async (customer) => {
    try {
      const id = customer.customerId;
      const poolDetails = await fetchPoolDetails(id);
      const customerData = {
        customer: customer,
        poolDetails: poolDetails || {},
      };
      console.log("Customer data to edit:", customerData);
      setCustomerToEdit(customerData);
      setEditModalOpen(true);
    }
    catch {
      console.error("Error fetching pool details");
    }
  };

  const onCustomerEdit = async (updatedData) => {
    try {
      const custId = updatedData.customerDetails.customerId;
      const response = await updateCustomer(updatedData, custId);
      if (response.status === 200) {
        setPopupGenericTitle("Success");
        setPopupGenericMessage("Customer details updated successfully");
        setPopupGenericVisible(true);
        loadCustomers();
      }
      else {
        setPopupGenericTitle("Error");
        setPopupGenericMessage("Error updating customer details");
        setPopupGenericVisible(true);
      }
    } catch (error) {
      console.error("Something went wrong:", error.message);
    }
  };

  const handleDeleteClick = (customer) => {
    setCustomerToDelete(customer);
    setDeletePopupVisible(true);
  };

  const onConfirmDelete = async () => {
    if (!customerToDelete) return;

    const isActive = customerToDelete.currRecInd === 'Y';

    try {
      console.log(isActive ? "Deactivating customer:" : "Permanently deleting customer:", customerToDelete.customerId);

      if (isActive) {
        // Deactivate active customer (soft delete)
        const poolDetails = await fetchPoolDetails(customerToDelete.customerId);

        const updatedData = {
          customerDetails: {
            ...customerToDelete,
            currRecInd: 'N', // Mark as inactive
          },
          poolDetails: poolDetails || {},
        };

        console.log("Sending deactivate request with data:", updatedData);
        const response = await updateCustomer(updatedData, customerToDelete.customerId);
        console.log("Deactivate response:", response);

        if (response.status === 200) {
          setDeletePopupVisible(false);
          setCustomerToDelete(null);
          await loadCustomers();
          setPopupGenericTitle("Success");
          setPopupGenericMessage("Customer deactivated successfully. The list has been refreshed.");
          setPopupGenericVisible(true);
        } else {
          setPopupGenericTitle("Error");
          setPopupGenericMessage("Error deactivating customer");
          setPopupGenericVisible(true);
        }
      } else {
        // Permanently delete inactive customer (hard delete)
        console.log("Sending permanent delete request for customer ID:", customerToDelete.customerId);

        // Check if customer has pool details or work orders that might prevent deletion
        const poolDetails = await fetchPoolDetails(customerToDelete.customerId);
        console.log("Pool details before deletion:", poolDetails);

        // Check for existing work orders
        const customerWorkOrders = workOrders.filter(
          wo => wo.workOrder && wo.workOrder.customerId === customerToDelete.customerId
        );
        console.log("Work orders before deletion:", customerWorkOrders);

        if (customerWorkOrders.length > 0) {
          setDeletePopupVisible(false);
          setCustomerToDelete(null);
          setPopupGenericTitle("Cannot Delete Customer");
          setPopupGenericMessage(
            <div>
              <p>This customer has {customerWorkOrders.length} associated work order(s) that must be deleted first.</p>
              <p style={{ marginTop: '10px', color: '#f59e0b' }}>
                Please delete all work orders for this customer before attempting to permanently delete the customer.
              </p>
              <p style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                You can view and delete work orders from the customer's detail page or the work orders tab.
              </p>
            </div>
          );
          setPopupGenericVisible(true);
          return;
        }

        const response = await deleteCustomer(customerToDelete.customerId, companyId);
        console.log("Delete response:", response);

        if (response.status === 200) {
          setDeletePopupVisible(false);
          setCustomerToDelete(null);
          await loadCustomers();
          setPopupGenericTitle("Success");
          setPopupGenericMessage("Customer permanently deleted from the database. This action cannot be undone.");
          setPopupGenericVisible(true);
        } else {
          setPopupGenericTitle("Error");
          setPopupGenericMessage("Error deleting customer");
          setPopupGenericVisible(true);
        }
      }
    } catch (error) {
      console.error(isActive ? "Error deactivating customer:" : "Error deleting customer:", error);
      console.error("Error details:", error.response?.data);

      // Extract meaningful error message
      let errorMessage = `Error ${isActive ? 'deactivating' : 'deleting'} customer`;
      let errorTitle = isActive ? "Cannot Deactivate Customer" : "Cannot Delete Customer";

      if (error.response?.data?.errorMessage) {
        errorMessage = error.response.data.errorMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Provide more helpful guidance for pool details deletion errors
      if (errorMessage.toLowerCase().includes('pool details')) {
        errorTitle = "Cannot Delete Customer";
        errorMessage = (
          <div>
            <p>{errorMessage}</p>
            <p style={{ marginTop: '10px', color: '#f59e0b' }}>
              This customer may have associated pool details or work orders that prevent deletion.
            </p>
            <p style={{ marginTop: '10px', fontSize: '0.9rem' }}>
              <strong>Possible solutions:</strong>
            </p>
            <ul style={{ textAlign: 'left', marginLeft: '20px', marginTop: '5px', fontSize: '0.9rem' }}>
              <li>Check if there are any active or completed work orders for this customer</li>
              <li>Try deleting associated work orders first</li>
              <li>Contact support if the issue persists</li>
            </ul>
          </div>
        );
      }

      setDeletePopupVisible(false);
      setCustomerToDelete(null);
      setPopupGenericTitle(errorTitle);
      setPopupGenericMessage(errorMessage);
      setPopupGenericVisible(true);
    }
  };

  const onCancelDelete = () => {
    setDeletePopupVisible(false);
    setCustomerToDelete(null);
  };

  const handleNavigateToScheduling = () => {
    setPopupVisible(false);
    navigate('/employee-scheduling');
  };

  // Handle bulk upload modal open
  const handleBulkUploadOpen = () => {
    setBulkUploadOpen(true);
  };

  // Handle bulk upload modal close
  const handleBulkUploadClose = () => {
    setBulkUploadOpen(false);
    setBulkUploadFile(null);
  };

  // Handle file upload for bulk import
  const handleBulkFileUpload = (e) => {
    const file = e.target.files[0];

    // Validate file type
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'application/x-iwork-numbers-sffnumbers', // .numbers
      ];
      const validExtensions = ['.xlsx', '.xls', '.numbers'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        setPopupGenericMessage("Please upload an Excel file (.xlsx or .xls) or Apple Numbers file (.numbers). Download the template, fill it in, and upload.");
        setPopupGenericTitle("Invalid File Type");
        setPopupGenericVisible(true);
        e.target.value = '';
        return;
      }

      // Apple Numbers files need to be exported as xlsx first
      if (fileExtension === '.numbers') {
        setPopupGenericMessage("Apple Numbers files (.numbers) are not directly supported. In Numbers, go to File → Export To → Excel (.xlsx), then upload the exported .xlsx file.");
        setPopupGenericTitle("Export Numbers as Excel");
        setPopupGenericVisible(true);
        e.target.value = '';
        return;
      }

      // Check file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setPopupGenericMessage("File size exceeds 10MB. Please upload a smaller file.");
        setPopupGenericTitle("File Too Large");
        setPopupGenericVisible(true);
        e.target.value = ''; // Clear the file input
        return;
      }
    }

    setBulkUploadFile(file);
  };

  // Validate Excel file client-side before upload
  const validateBulkUploadFile = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

          if (rows.length < 2) {
            resolve({ valid: false, errors: ['The file is empty or has no data rows.'], duplicateEmails: [] });
            return;
          }

          const headers = rows[0].map(h => String(h).trim());
          const headersLower = headers.map(h => h.toLowerCase());

          // Accept both friendly names and legacy technical names
          const getIdx = (...names) => {
            for (const name of names) {
              const idx = headersLower.indexOf(name.toLowerCase());
              if (idx !== -1) return idx;
            }
            return -1;
          };

          const firstNameIdx = getIdx('First Name', 'custFirstName');
          const lastNameIdx = getIdx('Last Name', 'custLastName');
          const emailIdx = getIdx('Email Address', 'Email', 'custEmail');
          const contactIdx = getIdx('Phone Number', 'Contact No', 'custContactNo');
          const addressIdx = getIdx('Address', 'custAddress');
          const cityIdx = getIdx('City', 'custCity');
          const stateIdx = getIdx('State', 'custState');
          const zipIdx = getIdx('Zip Code', 'custZip');
          const secondaryEmailIdx = getIdx('Secondary Email', 'secondaryEmail', 'custSecondaryEmail');

          // Validate required columns exist (accept either form)
          const requiredFieldChecks = [
            { idx: firstNameIdx, label: 'First Name' },
            { idx: lastNameIdx, label: 'Last Name' },
            { idx: emailIdx, label: 'Email Address' },
            { idx: contactIdx, label: 'Phone Number' },
            { idx: addressIdx, label: 'Address' },
            { idx: cityIdx, label: 'City' },
            { idx: stateIdx, label: 'State' },
            { idx: zipIdx, label: 'Zip Code' },
          ];
          const missingHeaders = requiredFieldChecks.filter(f => f.idx === -1).map(f => f.label);

          if (missingHeaders.length > 0) {
            resolve({
              valid: false,
              errors: [`Missing required columns: ${missingHeaders.join(', ')}. Please download and use the provided template.`],
              duplicateEmails: []
            });
            return;
          }

          // Pool column indices (all optional)
          const VALID_POOL_TYPES  = ['RESIDENTIAL', 'COMMERCIAL', 'POOL', 'POOLSPA'];
          const VALID_POOL_SHAPES = ['rectangle', 'circle', 'oval', 'square', 'kidney', 'l-shape', 'figure-8', 'roman', 'freeform'];
          const poolTypeIdx     = getIdx('Pool Type', 'poolType');
          const poolLengthIdx   = getIdx('Pool Length (ft)', 'poolLength');
          const poolWidthIdx    = getIdx('Pool Width (ft)', 'poolWidth');
          const poolHeightIdx   = getIdx('Average Depth (ft)', 'poolHeight');
          const poolShapeIdx    = getIdx('Pool Shape', 'poolShape');
          const poolCapacityIdx = getIdx('Pool Capacity (gal)', 'poolCapacity');
          const hasPoolColumns  = [poolTypeIdx, poolLengthIdx, poolWidthIdx, poolHeightIdx, poolShapeIdx].some(i => i !== -1);

          const errors = [];
          const seenEmails = new Map();
          const duplicateEmails = [];
          const phoneNumbers = [];
          let dataRowCount = 0;

          for (let i = 1; i < rows.length; i++) {
            const row = rows[i];
            if (!row || row.every(cell => cell === '' || cell === null || cell === undefined)) continue;

            dataRowCount++;
            const rowNum = i + 1;
            // Decode HTML entities (e.g. &amp; → &) and sanitize city (strip stray commas)
            const firstName = decodeHtmlEntities(String(row[firstNameIdx] ?? '').trim());
            const lastName = decodeHtmlEntities(String(row[lastNameIdx] ?? '').trim());
            const email = String(row[emailIdx] ?? '').trim().toLowerCase();
            const contact = String(row[contactIdx] ?? '').trim();
            const address = String(row[addressIdx] ?? '').trim();
            const city = sanitizeCity(String(row[cityIdx] ?? ''));
            const state = String(row[stateIdx] ?? '').trim();
            const zip = String(row[zipIdx] ?? '').trim();

            // Normalize phone: strip non-digits, remove leading +1 if 11 digits
            let normalizedContact = contact.replace(/\D/g, '');
            if (normalizedContact.length === 11 && normalizedContact.startsWith('1')) {
              normalizedContact = normalizedContact.substring(1);
            }

            if (!firstName) errors.push(`Row ${rowNum}: First Name is required.`);
            if (!lastName) errors.push(`Row ${rowNum}: Last Name is required.`);

            const namePattern = /^[A-Za-z\s&'\-]*$/;
            if (firstName && !namePattern.test(firstName)) {
              const badChars = [...new Set([...firstName].filter(c => !namePattern.test(c)))].join(', ');
              errors.push(`Row ${rowNum}: First Name "${firstName}" has invalid character(s): ${badChars} — only letters, spaces, &, ', and - are allowed.`);
            }
            if (lastName && !namePattern.test(lastName)) {
              const badChars = [...new Set([...lastName].filter(c => !namePattern.test(c)))].join(', ');
              errors.push(`Row ${rowNum}: Last Name "${lastName}" has invalid character(s): ${badChars} — only letters, spaces, &, ', and - are allowed.`);
            }
            if (city && !/^[A-Za-z\s]*$/.test(city)) {
              const badChars = [...new Set([...city].filter(c => !/[A-Za-z\s]/.test(c)))].join(', ');
              errors.push(`Row ${rowNum}: City "${city}" has invalid character(s): ${badChars} — only letters and spaces are allowed.`);
            }

            const secondaryEmail = secondaryEmailIdx !== -1 ? String(row[secondaryEmailIdx] ?? '').trim().toLowerCase() : '';
            if (secondaryEmail && email && secondaryEmail === email) {
              errors.push(`Row ${rowNum}: Primary email and secondary email must be different.`);
            }

            if (!email) {
              errors.push(`Row ${rowNum}: Email (custEmail) is required.`);
            } else if (!validateEmail(email).isValid) {
              errors.push(`Row ${rowNum}: Email "${email}" is not a valid email address.`);
            } else {
              if (seenEmails.has(email)) {
                errors.push(`Row ${rowNum}: Duplicate email "${email}" — already present in row ${seenEmails.get(email)} of this file.`);
              } else {
                seenEmails.set(email, rowNum);
                duplicateEmails.push(email);
              }
            }

            if (!contact) {
              errors.push(`Row ${rowNum}: Contact No (custContactNo) is required.`);
            } else if (normalizedContact.length !== 10) {
              errors.push(`Row ${rowNum}: Contact No must be a 10-digit phone number (got "${contact}").`);
            } else {
              phoneNumbers.push(normalizedContact);
            }

            if (!address) errors.push(`Row ${rowNum}: Address (custAddress) is required.`);
            if (!city) errors.push(`Row ${rowNum}: City (custCity) is required.`);
            if (!state) errors.push(`Row ${rowNum}: State (custState) is required.`);

            if (!zip) {
              errors.push(`Row ${rowNum}: Zip Code (custZip) is required.`);
            } else if (!validateZip(zip).isValid) {
              errors.push(`Row ${rowNum}: Zip Code must be a 5-digit number (got "${zip}").`);
            }

            // Validate pool fields if the template includes pool columns
            if (hasPoolColumns) {
              const poolType     = poolTypeIdx     !== -1 ? String(row[poolTypeIdx]     ?? '').trim() : '';
              const poolLength   = poolLengthIdx   !== -1 ? String(row[poolLengthIdx]   ?? '').trim() : '';
              const poolWidth    = poolWidthIdx    !== -1 ? String(row[poolWidthIdx]    ?? '').trim() : '';
              const poolHeight   = poolHeightIdx   !== -1 ? String(row[poolHeightIdx]   ?? '').trim() : '';
              const poolShape    = poolShapeIdx    !== -1 ? String(row[poolShapeIdx]    ?? '').trim() : '';
              const poolCapacity = poolCapacityIdx !== -1 ? String(row[poolCapacityIdx] ?? '').trim() : '';

              if (poolType && !VALID_POOL_TYPES.includes(poolType.toUpperCase())) {
                errors.push(`Row ${rowNum}: Pool Type "${poolType}" is invalid. Use: ${VALID_POOL_TYPES.join(', ')}.`);
              }
              if (poolShape && !VALID_POOL_SHAPES.includes(poolShape.toLowerCase())) {
                errors.push(`Row ${rowNum}: Pool Shape "${poolShape}" is invalid. Use: Rectangle, Circle, Oval, Square, Kidney, L-Shape, Figure-8, Roman, Freeform.`);
              }
              const numericPoolFields = [
                { val: poolLength, name: 'Pool Length' },
                { val: poolWidth,  name: 'Pool Width' },
                { val: poolHeight, name: 'Average Depth' },
                { val: poolCapacity, name: 'Pool Capacity' },
              ];
              numericPoolFields.forEach(({ val, name }) => {
                if (val && (isNaN(Number(val)) || Number(val) <= 0)) {
                  errors.push(`Row ${rowNum}: ${name} must be a positive number (got "${val}").`);
                }
              });
            }
          }

          if (dataRowCount === 0) {
            resolve({ valid: false, errors: ['The file has no data rows.'], duplicateEmails: [], phoneNumbers: [] });
            return;
          }

          resolve({ valid: errors.length === 0, errors, duplicateEmails, phoneNumbers });
        } catch (err) {
          resolve({ valid: false, errors: ['Failed to read the file. Please ensure it is a valid Excel file.'], duplicateEmails: [] });
        }
      };
      reader.onerror = () => resolve({ valid: false, errors: ['Failed to read the file.'], duplicateEmails: [] });
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle bulk upload submission
  const handleBulkUploadSubmit = async () => {
    if (!bulkUploadFile) {
      setPopupGenericMessage("Please select a file to upload.");
      setPopupGenericTitle("Alert");
      setPopupGenericVisible(true);
      return;
    }
    setBulkUploading(true);

    // Client-side pre-upload validation (fixes missing first/last name validation)
    const validation = await validateBulkUploadFile(bulkUploadFile);
    if (!validation.valid) {
      const errorContent = (
        <div>
          <p><strong>Please fix the following issues in the file before uploading:</strong></p>
          <ul style={{ textAlign: 'left', marginTop: '10px', paddingLeft: '20px', maxHeight: '200px', overflowY: 'auto' }}>
            {validation.errors.map((err, idx) => (
              <li key={idx} style={{ marginBottom: '5px', color: '#ef4444' }}>{err}</li>
            ))}
          </ul>
        </div>
      );
      setBulkUploading(false);
      setPopupGenericMessage(errorContent);
      setPopupGenericTitle("File Validation Error");
      setPopupGenericVisible(true);
      return;
    }

    // Block upload if duplicates found — mirrors single-customer duplicate check (email unique across customers + employees; phone unique across customers)
    const existingCustomerEmails = customers
      .map(c => (c.custObject?.custEmail || '').toLowerCase().trim())
      .filter(Boolean);
    const existingEmployeeEmails = employees
      .map(emp => (emp.email || '').toLowerCase().trim())
      .filter(Boolean);
    const emailsMatchingCustomers = validation.duplicateEmails.filter(email => existingCustomerEmails.includes(email));
    const emailsMatchingEmployees = validation.duplicateEmails.filter(email => existingEmployeeEmails.includes(email));

    const existingPhones = customers
      .map(c => normalizePhone(c.custObject?.custContactNo))
      .filter(p => p.length === 10);
    const phonesMatchingCustomers = (validation.phoneNumbers || []).filter(p => existingPhones.includes(p));

    if (emailsMatchingCustomers.length > 0 || emailsMatchingEmployees.length > 0 || phonesMatchingCustomers.length > 0) {
      const errorContent = (
        <div>
          <p><strong>Upload blocked: the following records already exist in the system.</strong></p>
          <p style={{ marginTop: '8px', color: '#ef4444' }}>Please remove or update these rows in the file before uploading.</p>
          {emailsMatchingCustomers.length > 0 && (
            <>
              <p style={{ color: '#ef4444', marginTop: '12px', fontWeight: 'bold' }}>
                Email(s) already used by existing customers:
              </p>
              <ul style={{ textAlign: 'left', marginTop: '6px', maxHeight: '120px', overflowY: 'auto', paddingLeft: '20px' }}>
                {emailsMatchingCustomers.map((email, i) => (
                  <li key={i} style={{ marginBottom: '4px' }}>{email}</li>
                ))}
              </ul>
            </>
          )}
          {emailsMatchingEmployees.length > 0 && (
            <>
              <p style={{ color: '#ef4444', marginTop: '12px', fontWeight: 'bold' }}>
                Email(s) already used by existing employees:
              </p>
              <ul style={{ textAlign: 'left', marginTop: '6px', maxHeight: '120px', overflowY: 'auto', paddingLeft: '20px' }}>
                {emailsMatchingEmployees.map((email, i) => (
                  <li key={i} style={{ marginBottom: '4px' }}>{email}</li>
                ))}
              </ul>
            </>
          )}
          {phonesMatchingCustomers.length > 0 && (
            <>
              <p style={{ color: '#ef4444', marginTop: '12px', fontWeight: 'bold' }}>
                Phone number(s) already used by existing customers:
              </p>
              <ul style={{ textAlign: 'left', marginTop: '6px', maxHeight: '120px', overflowY: 'auto', paddingLeft: '20px' }}>
                {phonesMatchingCustomers.map((phone, i) => (
                  <li key={i} style={{ marginBottom: '4px' }}>{phone}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      );
      setBulkUploading(false);
      setPopupGenericMessage(errorContent);
      setPopupGenericTitle("Duplicate Records Detected");
      setPopupGenericVisible(true);
      return;
    }

    try {
      const response = await postBulkUpload(bulkUploadFile, companyId);
      console.log("Bulk upload response:", response);

      // Close the upload modal first
      handleBulkUploadClose();
      // Small delay to allow backend DB transaction to fully commit before reloading
      await new Promise(resolve => setTimeout(resolve, 800));
      // Reload customers after successful upload
      await loadCustomers();

      // Extract result details from response
      const resultData = response.data?.data || response.data || {};
      const addedCount = resultData.addedCount || 0;
      const skippedCount = resultData.skippedCount || 0;
      const skippedCustomers = resultData.skippedCustomers || [];
      const deactivatedCount = resultData.deactivatedCount || 0;
      const deactivatedCustomers = resultData.deactivatedCustomers || [];
      const invalidCount = resultData.invalidCount || 0;
      const invalidRows = resultData.invalidRows || [];

      // Build detailed success message
      let messageContent;
      const hasSkipped = skippedCount > 0;
      const hasDeactivated = deactivatedCount > 0;
      const hasInvalid = invalidCount > 0;
      if (hasSkipped || hasDeactivated || hasInvalid) {
        messageContent = (
          <div>
            <p><strong>Successfully added {addedCount} customer(s).</strong></p>

            {hasSkipped && (
              <>
                <p style={{ color: '#f59e0b', marginTop: '15px', fontWeight: 'bold' }}>
                  {skippedCount} customer(s) were skipped because they already exist in the database (duplicate email addresses):
                </p>
                <ul style={{ textAlign: 'left', marginTop: '10px', maxHeight: '150px', overflowY: 'auto', paddingLeft: '20px' }}>
                  {skippedCustomers.map((name, index) => (
                    <li key={index} style={{ marginBottom: '5px' }}>{name}</li>
                  ))}
                </ul>
              </>
            )}

            {hasDeactivated && (
              <>
                <p style={{ color: '#ef4444', marginTop: '15px', fontWeight: 'bold' }}>
                  {deactivatedCount} customer(s) were skipped because they exist but are currently deactivated:
                </p>
                <ul style={{ textAlign: 'left', marginTop: '10px', maxHeight: '150px', overflowY: 'auto', paddingLeft: '20px' }}>
                  {deactivatedCustomers.map((name, index) => (
                    <li key={index} style={{ marginBottom: '5px' }}>{name}</li>
                  ))}
                </ul>
                <p style={{ color: '#ef4444', marginTop: '10px', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  Please activate these existing customers from the Inactive customer list instead of uploading them again.
                </p>
              </>
            )}

            {hasInvalid && (
              <>
                <p style={{ color: '#ef4444', marginTop: '15px', fontWeight: 'bold' }}>
                  {invalidCount} row(s) were skipped due to missing required fields (First Name or Last Name):
                </p>
                <ul style={{ textAlign: 'left', marginTop: '10px', maxHeight: '150px', overflowY: 'auto', paddingLeft: '20px' }}>
                  {invalidRows.map((msg, index) => (
                    <li key={index} style={{ marginBottom: '5px' }}>{msg}</li>
                  ))}
                </ul>
                <p style={{ color: '#ef4444', marginTop: '10px', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  Please fix these rows in your file and re-upload to add the missing customers.
                </p>
              </>
            )}

            <p style={{ marginTop: '15px' }}>The customer list has been refreshed.</p>
          </div>
        );
      } else {
        messageContent = (
          <div>
            <p><strong>Successfully added {addedCount} customer(s).</strong></p>
            <p style={{ marginTop: '10px' }}>The customer list has been refreshed.</p>
          </div>
        );
      }

      // Show success message after reload
      setPopupGenericMessage(messageContent);
      setPopupGenericTitle("Bulk Upload Complete");
      setPopupGenericVisible(true);
    } catch (error) {
      console.error("Bulk upload error:", error);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);

      // Try to extract error message from various possible locations
      let errorMessage = "An error occurred during bulk upload.";

      if (error.response?.data) {
        // Try different possible error message locations
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.errors) {
          errorMessage = JSON.stringify(error.response.data.errors);
        } else {
          // Show the full response if we can't find a specific message
          errorMessage = `Server error: ${JSON.stringify(error.response.data)}`;
        }
      } else if (error.message) {
        errorMessage = `Request failed: ${error.message}`;
      }

      setPopupGenericMessage(errorMessage);
      setPopupGenericTitle("Upload Error");
      setPopupGenericVisible(true);
    } finally {
      setBulkUploading(false);
    }
  };

  // Handle download template - creates Excel file with friendly headers
  const handleDownloadTemplate = () => {
    const headers = [
      'First Name', 'Last Name', 'Phone Number', 'Address',
      'City', 'State', 'Zip Code', 'Gate Code', 'Email Address', 'Secondary Email', 'Service Day'
    ];

    const sampleData = [
      ['John', 'Smith', '2105551234', '123 Main St', 'Phoenix', 'AZ', '85001', '#1234', 'john@email.com', '', 'Monday'],
      ['Jane', 'Doe',   '2129876543', '456 Oak Ave', 'Scottsdale', 'AZ', '85250', '', 'jane@email.com', 'jane.alt@email.com', 'Wednesday'],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...sampleData]);
    worksheet['!cols'] = [
      { wch: 15 }, { wch: 15 }, { wch: 14 }, { wch: 25 },
      { wch: 14 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 24 }, { wch: 24 }, { wch: 12 }
    ];

    // Add Instructions sheet
    const instructions = [
      ['Field', 'Required', 'Notes'],
      ['First Name', 'Yes', 'Text'],
      ['Last Name', 'Yes', 'Text'],
      ['Phone Number', 'Yes', '10-digit number (e.g. 2105551234 or 210-555-1234 or +1 (210) 555-1234)'],
      ['Address', 'Yes', 'Street address'],
      ['City', 'Yes', 'Text'],
      ['State', 'Yes', 'Text or 2-letter abbreviation (e.g. AZ)'],
      ['Zip Code', 'Yes', '5-digit zip code'],
      ['Gate Code', 'No', 'Gate/access code (optional)'],
      ['Email Address', 'Yes', 'Valid email address — customer receives login invitation'],
      ['Secondary Email', 'No', 'Optional second contact email'],
      ['Service Day', 'No', 'Monday / Tuesday / Wednesday / Thursday / Friday / Saturday / Sunday'],
    ];
    const instrSheet = XLSX.utils.aoa_to_sheet(instructions);
    instrSheet['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 65 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');
    XLSX.utils.book_append_sheet(workbook, instrSheet, 'Instructions');
    XLSX.writeFile(workbook, 'customer_import_template.xlsx');
  };

  const handleDownloadPoolTemplate = () => {
    const headers = [
      'First Name', 'Last Name', 'Phone Number', 'Address',
      'City', 'State', 'Zip Code', 'Gate Code', 'Email Address', 'Secondary Email', 'Service Day',
      'Pool Type', 'Pool Length (ft)', 'Pool Width (ft)', 'Average Depth (ft)', 'Pool Shape', 'Pool Capacity (gal)'
    ];

    const sampleData = [
      // First Name, Last Name, Phone Number, Address, City, State, Zip, Gate Code, Email, Secondary Email, Service Day, Pool Type, Length, Width, Depth, Shape, Capacity
      ['John', 'Smith', '2105551234', '123 Main St', 'Phoenix', 'AZ', '85001', '#1234', 'john@email.com', '', 'Monday', 'RESIDENTIAL', 16, 32, 5, 'Rectangle', ''],
      ['Jane', 'Doe', '2129876543', '456 Oak Ave', 'Scottsdale', 'AZ', '85250', '', 'jane@email.com', 'jane.alt@email.com', 'Wednesday', 'RESIDENTIAL', 15, 30, 5, 'Oval', ''],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...sampleData]);
    worksheet['!cols'] = [
      { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 25 },
      { wch: 14 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 24 }, { wch: 24 }, { wch: 12 },
      { wch: 16 }, { wch: 18 }, { wch: 16 }, { wch: 20 }, { wch: 14 }, { wch: 20 }
    ];

    // Add an Instructions sheet with allowed values
    const instructions = [
      ['Field', 'Required', 'Allowed Values / Notes'],
      ['First Name',       'Yes', 'Text'],
      ['Last Name',        'Yes', 'Text'],
      ['Phone Number',     'Yes', '10-digit US number. Formats accepted: 2105551234 or 210-555-1234 or +1 (210) 555-1234'],
      ['Address',          'Yes', 'Street address'],
      ['City',             'Yes', 'Text'],
      ['State',            'Yes', 'Text or 2-letter abbreviation (e.g. AZ)'],
      ['Zip Code',         'Yes', '5-digit zip code'],
      ['Gate Code',        'No',  'Gate/access code (optional)'],
      ['Email Address',    'Yes', 'Valid email address — customer receives login invitation'],
      ['Secondary Email',  'No',  'Optional second contact email'],
      ['Service Day',      'No',  'Monday / Tuesday / Wednesday / Thursday / Friday / Saturday / Sunday'],
      ['Pool Type',        'No',  'RESIDENTIAL | COMMERCIAL | POOL | POOLSPA'],
      ['Pool Length (ft)', 'No',  'Number in feet (e.g. 16)'],
      ['Pool Width (ft)',  'No',  'Number in feet (e.g. 32)'],
      ['Average Depth (ft)', 'No', 'Average depth in feet (e.g. 5)'],
      ['Pool Shape',       'No',  'Rectangle | Circle | Oval | Square | Kidney | L-Shape | Figure-8 | Roman | Freeform'],
      ['Pool Capacity (gal)', 'No', 'Gallons — leave blank to auto-calculate from dimensions'],
    ];

    const instrSheet = XLSX.utils.aoa_to_sheet(instructions);
    instrSheet['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 60 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');
    XLSX.utils.book_append_sheet(workbook, instrSheet, 'Instructions');
    XLSX.writeFile(workbook, 'customer_pool_import_template.xlsx');
  };

  // Bulk Operations Handlers
  const handleBulkUpdateServiceDay = async (customers, newServiceDay) => {
    try {
      // Update each customer's service day
      for (const customer of customers) {
        const poolDetails = await fetchPoolDetails(customer.id);
        const updatedData = {
          customerDetails: {
            ...customer.custObject,
            serviceDay: newServiceDay
          },
          poolDetails: poolDetails || {}
        };
        await updateCustomer(updatedData, customer.id);
      }

      setPopupGenericTitle("Success");
      setPopupGenericMessage(`Successfully updated service day for ${customers.length} customer(s)`);
      setPopupGenericVisible(true);
      setSelectedCustomerIds([]);
      loadCustomers();
    } catch (error) {
      console.error("Bulk update error:", error);
      setPopupGenericTitle("Error");
      setPopupGenericMessage("Error updating service days. Please try again.");
      setPopupGenericVisible(true);
    }
  };

  const handleBulkDeactivate = async (customers) => {
    try {
      for (const customer of customers) {
        const poolDetails = await fetchPoolDetails(customer.id);
        const updatedData = {
          customerDetails: {
            ...customer.custObject,
            currRecInd: 'N'
          },
          poolDetails: poolDetails || {}
        };
        await updateCustomer(updatedData, customer.id);
      }

      setPopupGenericTitle("Success");
      setPopupGenericMessage(`Successfully deactivated ${customers.length} customer(s)`);
      setPopupGenericVisible(true);
      setSelectedCustomerIds([]);
      loadCustomers();
    } catch (error) {
      console.error("Bulk deactivate error:", error);
      setPopupGenericTitle("Error");
      setPopupGenericMessage("Error deactivating customers. Please try again.");
      setPopupGenericVisible(true);
    }
  };

  const handleBulkDelete = async (customers) => {
    try {
      let successCount = 0;
      let failedCustomers = [];

      for (const customer of customers) {
        try {
          await deleteCustomer(customer.id, companyId);
          successCount++;
        } catch (error) {
          console.error(`Failed to delete customer ${customer.name}:`, error);
          const errorMsg = error.response?.data?.errorMessage || error.response?.data?.message || error.message;
          failedCustomers.push({ name: customer.name, reason: errorMsg });
        }
      }

      setSelectedCustomerIds([]);
      await loadCustomers();

      if (failedCustomers.length === 0) {
        setPopupGenericTitle("Success");
        setPopupGenericMessage(`Successfully deleted ${successCount} customer(s) permanently from the database.`);
      } else if (successCount === 0) {
        setPopupGenericTitle("Cannot Delete Customers");
        const errorDetails = failedCustomers.map(f => `• ${f.name}: ${f.reason}`).join('\n');
        setPopupGenericMessage(
          <div>
            <p>Failed to delete {failedCustomers.length} customer(s):</p>
            <div style={{ marginTop: '10px', fontSize: '0.9rem', textAlign: 'left', whiteSpace: 'pre-line' }}>
              {errorDetails}
            </div>
          </div>
        );
      } else {
        setPopupGenericTitle("Partial Success");
        const errorDetails = failedCustomers.map(f => `• ${f.name}: ${f.reason}`).join('\n');
        setPopupGenericMessage(
          <div>
            <p>Successfully deleted {successCount} customer(s).</p>
            <p style={{ marginTop: '10px', color: '#f59e0b' }}>Failed to delete {failedCustomers.length} customer(s):</p>
            <div style={{ marginTop: '10px', fontSize: '0.9rem', textAlign: 'left', whiteSpace: 'pre-line' }}>
              {errorDetails}
            </div>
          </div>
        );
      }
      setPopupGenericVisible(true);
    } catch (error) {
      console.error("Bulk delete error:", error);
      setPopupGenericTitle("Error");
      setPopupGenericMessage("Unexpected error during bulk delete. Please try again.");
      setPopupGenericVisible(true);
    }
  };

  const handleExportComplete = (format) => {
    setPopupGenericTitle("Success");
    setPopupGenericMessage(`Successfully exported ${selectedCustomerIds.length} customer(s) to ${format}`);
    setPopupGenericVisible(true);
  };

  // Get selected customers
  const selectedCustomers = customers.filter(c => selectedCustomerIds.includes(c.id));

  // Debug: Log customers state at render time
  console.log('ViewCustomers RENDER - customers.length:', customers.length);
  console.log('ViewCustomers RENDER - statusFilter:', statusFilter);

  return (
    <div className='customer-list'>
      {/* Customer Metrics Dashboard */}
      <Fade in={true} timeout={800}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 3 },
            background: 'linear-gradient(135deg, #003366 0%, #004080 100%)',
            borderRadius: 2
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              mb: 2,
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <TrendingUpIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> Customer Overview
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={6} sm={6} md={3}>
              <Grow in={true} timeout={600}>
                <Paper
                  elevation={0}
                  onClick={() => handleMetricClick('total')}
                  sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    textAlign: 'center',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <GroupIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: '#003366', mb: { xs: 0.5, sm: 1 } }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#003366', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {customerMetrics.total}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                    Total Customers
                  </Typography>
                </Paper>
              </Grow>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Grow in={true} timeout={800}>
                <Paper
                  elevation={0}
                  onClick={() => handleMetricClick('newThisMonth')}
                  sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    textAlign: 'center',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <GroupAddIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: '#10B981', mb: { xs: 0.5, sm: 1 } }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#10B981', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {customerMetrics.newThisMonth}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                    New This Month
                  </Typography>
                </Paper>
              </Grow>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Grow in={true} timeout={1000}>
                <Paper
                  elevation={0}
                  onClick={() => handleMetricClick('active')}
                  sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    textAlign: 'center',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: '#2196F3', mb: { xs: 0.5, sm: 1 } }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196F3', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {customerMetrics.active}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                    Active Customers
                  </Typography>
                </Paper>
              </Grow>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Grow in={true} timeout={1200}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Healthy Service Rate
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        Percentage of customers serviced within the last 7 days. Click to view healthy customers.
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', opacity: 0.9 }}>
                        Calculated in real-time from service records.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Paper
                    elevation={0}
                    onClick={() => handleMetricClick('healthy')}
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      textAlign: 'center',
                      background: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: '#F59E0B', mb: { xs: 0.5, sm: 1 } }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#F59E0B', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {customerMetrics.healthyPercentage}%
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      Healthy Service Rate
                    </Typography>
                  </Paper>
                </Tooltip>
              </Grow>
            </Grid>
          </Grid>
        </Paper>
      </Fade>

      {/* Customer Analytics Charts */}
      <CustomerAnalytics
        customers={customers.map(c => ({
          ...c,
          healthStatus: customerHealthStatus[c.id] || 'none'
        }))}
        customerHealthStatus={customerHealthStatus}
      />

      {/* Advanced Filters */}
      <AdvancedFilters
        customers={customers.map(c => ({
          ...c,
          healthStatus: customerHealthStatus[c.id] || 'none',
          serviceDay: customerScheduleMap[c.id]?.serviceDay
        }))}
        healthStatusFilter={healthStatusFilter}
        serviceDaysFilter={serviceDaysFilter}
        cityFilter={cityFilter}
        onHealthStatusChange={setHealthStatusFilter}
        onServiceDaysChange={setServiceDaysFilter}
        onCityChange={setCityFilter}
      />

      {/* Bulk Actions Toolbar */}
      <BulkActionsToolbar
        selectedCustomers={selectedCustomers}
        onClearSelection={() => setSelectedCustomerIds([])}
        onBulkUpdateServiceDay={handleBulkUpdateServiceDay}
        onBulkDeactivate={handleBulkDeactivate}
        onBulkDelete={handleBulkDelete}
        onExportComplete={handleExportComplete}
      />

      {viewMode === 'list' ? (
        <Box ref={dataTableRef}>
          <DataTable
            rows={filteredCustomers} // Pass filtered customers to the DataTable
            columns={columns}
            title="Manage Customers"
            checkboxSelection
            rowSelectionModel={selectedCustomerIds}
            onRowSelectionModelChange={(newSelection) => setSelectedCustomerIds(newSelection)}
            headerActions={
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                onClick={handleAddCustomer}
                fullWidth
                sx={{
                  backgroundColor: '#003366',
                  '&:hover': {
                    backgroundColor: '#002244',
                  },
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 12px', sm: '6px 16px' }
                }}
              >
                Add Customer
              </Button>
              <Tooltip title="Upload a CSV or Excel file to add customers in bulk" arrow>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<UploadIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                  onClick={handleBulkUploadOpen}
                  fullWidth
                  sx={{
                    backgroundColor: '#006644',
                    '&:hover': {
                      backgroundColor: '#004422',
                    },
                    textTransform: 'none',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 12px', sm: '6px 16px' }
                  }}
                >
                  Bulk Import Customers
                </Button>
              </Tooltip>
            </>
          }
          searchComponent={
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(event, newValue) => {
                if (newValue !== null) {
                  setViewMode(newValue);
                }
              }}
              aria-label="view mode"
              size="small"
              sx={{
                backgroundColor: '#fff',
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  padding: { xs: '4px 8px', sm: '6px 12px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '&.Mui-selected': {
                    backgroundColor: '#10B981',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#059669',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="list">
                <ViewListIcon sx={{ mr: { xs: 0, sm: 0.5 }, fontSize: { xs: 16, sm: 18 } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>List</Box>
              </ToggleButton>
              <ToggleButton value="map">
                <MapIcon sx={{ mr: { xs: 0, sm: 0.5 }, fontSize: { xs: 16, sm: 18 } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Map</Box>
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(event, newValue) => {
                if (newValue !== null) {
                  setStatusFilter(newValue);
                }
              }}
              aria-label="customer status filter"
              size="small"
              sx={{
                backgroundColor: '#fff',
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  padding: { xs: '4px 8px', sm: '6px 16px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '&.Mui-selected': {
                    backgroundColor: '#003366',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#002244',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="inactive">Inactive</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              placeholder="Search by Customer Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: '#fff',
                borderRadius: '5px',
                width: { xs: '100%', sm: '300px' },
                minWidth: { xs: '200px', sm: 'auto' },
                flex: { xs: '1 1 100%', sm: '0 1 auto' }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        }
        />
        </Box>
      ) : (
        <Box ref={dataTableRef}>
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, mt: { xs: 2, sm: 0 } }}>
            <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#003366', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Customer Locations Map
              </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                onClick={handleAddCustomer}
                fullWidth
                sx={{
                  backgroundColor: '#003366',
                  '&:hover': {
                    backgroundColor: '#002244',
                  },
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 12px', sm: '6px 16px' }
                }}
              >
                Add Customer
              </Button>
              <Tooltip title="Upload a CSV or Excel file to add customers in bulk" arrow>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<UploadIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                  onClick={handleBulkUploadOpen}
                  fullWidth
                  sx={{
                    backgroundColor: '#006644',
                    '&:hover': {
                      backgroundColor: '#004422',
                    },
                    textTransform: 'none',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 12px', sm: '6px 16px' }
                  }}
                >
                  Bulk Import Customers
                </Button>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ mb: 2, display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(event, newValue) => {
                if (newValue !== null) {
                  setViewMode(newValue);
                }
              }}
              aria-label="view mode"
              size="small"
              sx={{
                backgroundColor: '#fff',
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  padding: { xs: '4px 8px', sm: '6px 12px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '&.Mui-selected': {
                    backgroundColor: '#10B981',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#059669',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="list">
                <ViewListIcon sx={{ mr: { xs: 0, sm: 0.5 }, fontSize: { xs: 16, sm: 18 } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>List</Box>
              </ToggleButton>
              <ToggleButton value="map">
                <MapIcon sx={{ mr: { xs: 0, sm: 0.5 }, fontSize: { xs: 16, sm: 18 } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Map</Box>
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(event, newValue) => {
                if (newValue !== null) {
                  setStatusFilter(newValue);
                }
              }}
              aria-label="customer status filter"
              size="small"
              sx={{
                backgroundColor: '#fff',
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  padding: { xs: '4px 8px', sm: '6px 16px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '&.Mui-selected': {
                    backgroundColor: '#003366',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#002244',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="inactive">Inactive</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              placeholder="Search by Customer Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: '#fff',
                borderRadius: '5px',
                width: { xs: '100%', sm: '300px' },
                minWidth: { xs: '200px', sm: 'auto' },
                flex: { xs: '1 1 100%', sm: '0 1 auto' }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <CustomerMap
            customers={filteredCustomers}
            customerHealthStatus={customerHealthStatus}
            getHealthColor={getHealthColor}
            getHealthLabel={getHealthLabel}
          />
        </Paper>
        </Box>
      )}

      {selectedCustomer && (
        <PopupMenu
          isVisible={isPopupVisible}
          PersonData={selectedCustomer}
          onClose={() => setPopupVisible(false)}
          employeeInfo={customerEmployeeInfo}
          gateCodes={customerGateCodes}
          onNavigateToScheduling={handleNavigateToScheduling}
        />
      )}

      {isEditModalOpen && (
        <CustomerModal
          open={isEditModalOpen}
          onClose={handleCancelEdit}
          onCustomerAdded={loadCustomers}
          initialData={customerToEdit}
          onCustomerEdit={onCustomerEdit}
        />
      )}
      {isPopupGenericVisible && (
        <PopupGeneric
          isVisible={isPopupGenericVisible}
          onClose={() => setPopupGenericVisible(false)}
          title={popupGenericTitle}
          content={typeof popupGenericMessage === 'string' ? <p>{popupGenericMessage}</p> : popupGenericMessage}
          actions={
            <button
              onClick={() => setPopupGenericVisible(false)}
              style={{
                marginTop: '10px',
                padding: '5px 10px',
                borderRadius: '5px',
                backgroundColor: '#003366',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              OK
            </button>
          }
        />
      )}

      {/* Deactivate/Delete Confirmation Popup */}
      {isDeletePopupVisible && customerToDelete && (
        <PopupGeneric
          isVisible={isDeletePopupVisible}
          onClose={onCancelDelete}
          title={customerToDelete.currRecInd === 'Y' ? "Deactivate Customer" : "Permanently Delete Customer"}
          content={
            <div>
              {customerToDelete.currRecInd === 'Y' ? (
                <>
                  <p>Are you sure you want to deactivate this customer?</p>
                  <p><strong>{customerToDelete?.custFirstName} {customerToDelete?.custLastName}</strong></p>
                  <p style={{ color: '#f59e0b', marginTop: '10px' }}>
                    This will mark the customer as inactive and they will no longer be visible in the active customer list.
                  </p>
                </>
              ) : (
                <>
                  <p>Are you sure you want to <strong>permanently delete</strong> this customer?</p>
                  <p><strong>{customerToDelete?.custFirstName} {customerToDelete?.custLastName}</strong></p>
                  <p style={{ color: '#ef4444', marginTop: '10px', fontWeight: 'bold' }}>
                    ⚠️ WARNING: This action cannot be undone!
                  </p>
                  <p style={{ color: '#ef4444', marginTop: '5px' }}>
                    All customer information, including pool details and service history, will be permanently deleted from the database.
                  </p>
                </>
              )}
            </div>
          }
          actions={
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={onConfirmDelete}
                style={{
                  padding: '8px 16px',
                  borderRadius: '5px',
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
              >
                {customerToDelete.currRecInd === 'Y' ? 'Yes, Deactivate' : 'Yes, Permanently Delete'}
              </button>
              <button
                onClick={onCancelDelete}
                style={{
                  padding: '8px 16px',
                  borderRadius: '5px',
                  backgroundColor: '#666',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          }
        />
      )}

      {/* Routes Popup */}
      <WorkOrdersPopup
        customerId={null}
        open={showWorkOrders}
        onClose={() => setShowWorkOrders(false)}
      />

      {/* Bulk Upload Modal */}
      <Dialog open={bulkUploadOpen} onClose={bulkUploading ? undefined : handleBulkUploadClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Import Customers</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload an Excel file (.xlsx) to add multiple customers at once. Choose a template below, fill it in, then upload.
            </Typography>

            {/* Template selection with descriptions */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Step 1 — Download a Template
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
                <Button variant="outlined" size="small" onClick={handleDownloadTemplate} sx={{ mb: 0.5 }}>
                  Customer Only Template
                </Button>
                <Typography variant="caption" display="block" color="text.secondary">
                  Basic customer info: name, phone, address, email, gate code, service day. Use this when you don't have pool dimensions yet.
                </Typography>
              </Box>
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
                <Button variant="outlined" size="small" onClick={handleDownloadPoolTemplate} sx={{ mb: 0.5 }}>
                  Customer + Pool Template
                </Button>
                <Typography variant="caption" display="block" color="text.secondary">
                  Includes pool details (type, dimensions, shape). Pool capacity auto-calculates if left blank. Includes an Instructions tab.
                </Typography>
              </Box>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Step 2 — Fill & Upload
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Required: First Name, Last Name, Phone Number, Address, City, State, Zip Code, Email Address.
              Phone number can be in any US format: <strong>2105551234</strong>, <strong>210-555-1234</strong>, or <strong>+1 (210) 555-1234</strong>.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: 'warning.dark', bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.light', borderRadius: 1, px: 1.5, py: 1 }}>
              Make sure all addresses are accurate and complete. Incorrect or incomplete addresses will prevent customers from appearing on the map.
            </Typography>
          </Box>

          <Box sx={{ mt: 1, mb: 2 }}>
            <input
              type="file"
              id="bulk-upload-file-input"
              accept=".xlsx, .xls, .numbers, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={handleBulkFileUpload}
              style={{ display: 'none' }}
              disabled={bulkUploading}
            />
            <label htmlFor="bulk-upload-file-input">
              <Button
                variant="contained"
                component="span"
                size="large"
                disabled={bulkUploading}
                sx={{ fontWeight: 'bold', fontSize: '1rem', textTransform: 'none' }}
              >
                Choose File
              </Button>
            </label>
          </Box>
          {bulkUploadFile && (
            <Typography variant="body2" sx={{ mt: 1, color: 'success.main', fontWeight: 'bold' }}>
              Selected: {bulkUploadFile.name}
            </Typography>
          )}
          {bulkUploading && (
            <Typography variant="body2" sx={{ mt: 1, color: 'primary.main', fontWeight: 'bold' }}>
              Uploading... please wait. Do not close this dialog.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkUploadClose} disabled={bulkUploading}>Cancel</Button>
          <Button
            onClick={handleBulkUploadSubmit}
            color="primary"
            variant="contained"
            disabled={!bulkUploadFile || bulkUploading}
          >
            {bulkUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ViewCustomers;